/*
 * @Description:校验名称唯一性相关api
 * @Version: 2.0
 * @Autor: 纪楠楠 jinannan@qfei.cn
 * @Date: 2024-03-21 10:22:11
 * @LastEditors: 纪楠楠 jinannan@qfei.cn
 */
export default function useFlowStage() {
  // 转成树结构
  const buildTree = async (nodes, edges) => {
    // 创建一个节点映射表，以便快速查找节点
    const nodeMap = {};
    nodes.forEach((node) => {
      node.children = [];
      nodeMap[node.id] = node;
    });
    // 处理边的关系，将边的信息整合到节点树中
    edges.forEach((edge) => {
      const sourceNode = nodeMap[edge.source];
      const targetNode = nodeMap[edge.target];

      if (sourceNode && targetNode) {
        sourceNode.children.push({
          ...targetNode,
          edgeInfo: {
            id: edge.id,
            animated: edge.animated,
            style: edge.style
          }
        });
      }
    });
    // 找到树的根节点（没有其他节点指向的节点）
    const rootNodes = nodes.filter((node) => !edges.some((edge) => edge.target === node.id));
    return rootNodes;
  };
  // 增加level-递归
  const addLevelToTree = (node, currentLevel = 1) => {
    node.level = currentLevel;
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        addLevelToTree(child, currentLevel + 1);
      });
    }
  };
  // 增加level
  const getLevelToTree = async (treeData) => {
    treeData.forEach((rootNode) => addLevelToTree(rootNode));
    return treeData;
  };
  // 树结构 转换层级 并去除children
  const flattenTreeByLevel = async (treeData2) => {
    const levels = [];
    function traverse(node, level) {
      // 确保当前层级存在于 levels 数组中
      if (!levels[level]) {
        levels[level] = [];
      }
      // 处理当前节点，移除 children 属性
      const { children, ...nodeWithoutChildren } = node;
      levels[level].push(nodeWithoutChildren);
      // 遍历子节点
      if (children && children.length > 0) {
        children.forEach((child) => traverse(child, level + 1));
      }
    }
    // 从根节点开始遍历
    treeData2.forEach((rootNode) => traverse(rootNode, 0));
    return levels;
  };
  // 确保每个 id 只在最后出现的层级中存在，并且在之前的层级中不能重复出现
  const filterUniqueIds = async (layers) => {
    // 用于存储已出现的节点ID
    const seenIds = new Set();
    // 反向遍历每一层，以确保最后出现的节点ID在最终层级中保留
    for (let i = layers.length - 1; i >= 0; i--) {
      layers[i] = layers[i].filter((node) => {
        if (seenIds.has(node.id)) {
          return false; // 过滤掉在之后层级已出现的ID
        }
        return true;
      });
      // 将当前层级的ID加入已出现的集合
      layers[i].forEach((node) => seenIds.add(node.id));
    }
    return layers;
  };
  // 去重
  const deduplicateLayers = async (layers) => {
    const seenIds = new Set();
    const filteredLayers = layers.map(() => []);
    // 从最后一层开始向前处理
    for (let i = layers.length - 1; i >= 0; i--) {
      layers[i].forEach((node) => {
        if (!seenIds.has(node.id)) {
          seenIds.add(node.id);
          filteredLayers[i].push(node);
        }
      });
    }
    return filteredLayers;
  };
  // 去除重复的X轴 (假设压成一条线)
  const uniqueNodesFun = (xArrayCopy) => {
    const uniqueNodes = [];
    const xPoints = new Map();
    for (const node of xArrayCopy) {
      if (!xPoints.has(node.position.x)) {
        xPoints.set(node.position.x, true);
        uniqueNodes.push(node);
      }
    }
    uniqueNodes.sort((a, b) => a.position.x - b.position.x);
    return uniqueNodes;
  };
  // 生成贝塞尔
  const beSelC = (x1, y1, x2, y2) => {
    const Mave = (x1 + x2) / 2;
    const C1 = `C${Mave},${y1} ${Mave},${y2} ${x2},${y2}`;
    return C1;
  };
  // 生成线
  const generatePath = (...points) => {
    const start = `M${points[0].x},${points[0].y}`;
    const curveSegments = [];
    for (let i = 0; i < points.length - 1; i++) {
      curveSegments.push(beSelC(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y));
    }
    return [start, ...curveSegments].join(' ');
  };
  return {
    deduplicateLayers,
    filterUniqueIds,
    flattenTreeByLevel,
    getLevelToTree,
    buildTree,
    uniqueNodesFun,
    beSelC,
    generatePath
  };
}
